import type { GovernmentResult } from '@creanizer/types'
import { Building2, Stamp, ScrollText, Calendar, Shield, FileCheck } from 'lucide-react'

interface GovernmentViewerProps {
  document: GovernmentResult
}

export function GovernmentViewer({ document }: GovernmentViewerProps) {
  // データ検証
  if (!document || !document.header || !document.title) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-4">政府公式文書</h3>
        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
          {JSON.stringify(document, null, 2)}
        </pre>
      </div>
    )
  }
  
  return (
    <div className="max-w-4xl mx-auto bg-white shadow-2xl rounded-lg overflow-hidden">
      {/* 政府機関ヘッダー */}
      <div className="bg-gradient-to-r from-red-800 to-red-900 text-white p-6">
        <div className="flex items-center justify-center mb-4">
          <Shield className="w-12 h-12" />
        </div>
        <h1 className="text-2xl font-bold text-center mb-2">
          {document.header?.nation || '政府'}
        </h1>
        <h2 className="text-lg text-center text-red-100">
          {document.header?.agency || '機関'}
        </h2>
      </div>
      
      {/* 文書情報バー */}
      <div className="bg-gray-900 text-white px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <ScrollText className="w-5 h-5" />
            <span className="font-medium">{document.header.documentType}</span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div>
              <span className="text-gray-400">文書番号: </span>
              <span className="font-mono">{document.header.number}</span>
            </div>
            <div>
              <span className="text-gray-400">公布日: </span>
              <span>{document.header.date}</span>
            </div>
            {document.header.effectiveDate && (
              <div>
                <span className="text-gray-400">施行日: </span>
                <span>{document.header.effectiveDate}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* 法的ステータス */}
      {document.metadata?.legalStatus && (
        <div className="bg-yellow-50 border-b-2 border-yellow-400 px-6 py-2">
          <div className="flex items-center gap-2">
            <FileCheck className="w-4 h-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800">
              法的効力: {document.metadata.legalStatus}
            </span>
          </div>
        </div>
      )}
      
      {/* 文書本体 */}
      <div className="p-8">
        {/* タイトル */}
        <h1 className="text-3xl font-bold text-center text-gray-900 mb-8 pb-4 border-b-4 border-double border-gray-800">
          {document.title}
        </h1>
        
        {/* 前文 */}
        {document.preamble && (
          <div className="mb-8">
            <div className="p-6 bg-gray-50 border-l-4 border-red-600 rounded">
              <p className="text-gray-800 leading-relaxed text-justify italic">
                {document.preamble}
              </p>
            </div>
          </div>
        )}
        
        {/* 条文 */}
        {document.articles && document.articles.length > 0 && (
          <div className="space-y-6">
            {document.articles.map((article, index) => (
              <div key={index} className="border-l-2 border-gray-300 pl-6">
                <h3 className="font-bold text-lg text-gray-900 mb-3">
                  第{article.number}条
                  {article.title && ` （${article.title}）`}
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  {article.content}
                </p>
                
                {/* 項 */}
                {article.subsections && article.subsections.length > 0 && (
                  <div className="ml-4 space-y-2">
                    {article.subsections.map((subsection, subIndex) => (
                      <div key={subIndex} className="flex gap-3">
                        <span className="font-medium text-gray-600 min-w-[30px]">
                          {subsection.number}.
                        </span>
                        <p className="text-gray-700">
                          {subsection.content}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* 改正履歴 */}
        {document.metadata?.amendedBy && document.metadata.amendedBy.length > 0 && (
          <div className="mt-8 p-4 bg-blue-50 rounded">
            <h3 className="font-bold text-gray-900 mb-2">改正履歴</h3>
            <ul className="list-disc list-inside space-y-1">
              {document.metadata.amendedBy.map((amendment, index) => (
                <li key={index} className="text-sm text-gray-700">
                  {amendment}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* 署名欄 */}
        <div className="mt-12 pt-8 border-t-4 border-double border-gray-800">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {document.signatories.map((signatory, index) => (
              <div key={index} className="text-center">
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-1">{signatory.title}</p>
                  <p className="font-bold text-lg text-gray-900">
                    {signatory.name}
                  </p>
                </div>
                {signatory.signature && (
                  <div className="border-b-2 border-gray-800 mx-auto w-48 pb-2">
                    <p className="font-signature text-2xl">
                      {signatory.signature}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* 公印 */}
          {document.seal && (
            <div className="mt-8 flex justify-center">
              <div className="relative">
                <div className="w-32 h-32 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
                  <div className="text-white text-center">
                    <Stamp className="w-16 h-16 mx-auto mb-1" />
                    <p className="text-xs font-bold">公印</p>
                  </div>
                </div>
                <div className="absolute inset-0 border-4 border-red-800 rounded-full"></div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* フッター */}
      <div className="bg-gray-100 px-6 py-4">
        <div className="flex justify-between text-xs text-gray-600">
          <span>文書種別: {document.header.documentType}</span>
          <span>文字数: {document.metadata.wordCount}</span>
          <span>生成日時: {document.metadata.generatedAt}</span>
        </div>
      </div>
      
      <style>{`
        .font-signature {
          font-family: 'Brush Script MT', cursive;
          font-style: italic;
        }
      `}</style>
    </div>
  )
}